import { useState } from 'react'
import { formatClientName } from '@/lib/format'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useProjectCreateWizard,
  type ProjectCreateClientType,
  type ProjectCreateCivilityId,
  type ProjectCreateLanguageId,
} from '../ProjectCreateWizardContext'
import { ProjectCreateNavigation } from '../ProjectCreateNavigation'

export function Step2ClientDetails({
  onNext,
  onBack,
  currentStep,
  languageOptions,
  civilityOptions,
}: {
  onNext: () => void
  onBack: () => void
  currentStep: number
  languageOptions: Array<{ id: ProjectCreateLanguageId; label: string }>
  civilityOptions: Array<{ id: ProjectCreateCivilityId; label: string }>
}) {
  const { draft, setClientField } = useProjectCreateWizard()
  const [lastNameWasEmpty, setLastNameWasEmpty] = useState(true)

  const canGoNext =
    draft.client.lastName.trim().length > 0 &&
    draft.client.phone.trim().length > 0 &&
    draft.client.documentLanguageId.trim().length > 0

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Informations du client</h2>
        <p className="text-muted-foreground">Coordonnées principales</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="space-y-2">
          <Label>Type de client</Label>
          <RadioGroup
            value={draft.client.clientType}
            onValueChange={(v) => setClientField('clientType', v as ProjectCreateClientType)}
            className="flex gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="particulier" id="client_type_particulier" data-testid="client-type-particulier" />
              <Label htmlFor="client_type_particulier">Particulier</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="professionnel" id="client_type_professionnel" data-testid="client-type-professionnel" />
              <Label htmlFor="client_type_professionnel">Professionnel</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Designation + Name — individuals */}
        {draft.client.clientType === 'particulier' && (
          <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Nom du client — tel qu'il apparaît sur les documents
            </p>

            {/* Quick-pick chips */}
            <div className="flex gap-2 flex-wrap">
              {['M. & Mme', 'M.', 'Mme', 'M. & M.', 'Mme & Mme'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const next = draft.client.designation === opt ? '' : opt
                    setClientField('designation', next)
                  }}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    draft.client.designation === opt
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:border-primary'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Designation + Name side by side */}
            <div className="grid grid-cols-[160px_1fr] gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Désignation</Label>
                <Input
                  data-testid="client-designation-input"
                  placeholder="M. & Mme"
                  value={draft.client.designation}
                  onChange={(e) => setClientField('designation', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nom *</Label>
                <Input
                  data-testid="client-name-input"
                  placeholder="Dupont"
                  value={draft.client.name}
                  onChange={(e) => {
                    setClientField('name', e.target.value)
                    if (lastNameWasEmpty) {
                      setClientField('lastName', e.target.value)
                    }
                  }}
                />
              </div>
            </div>

            {/* Live preview */}
            {(draft.client.designation || draft.client.name) && (
              <p className="text-xs text-muted-foreground">
                Sur les documents :{' '}
                <span className="font-medium text-foreground">
                  {formatClientName(draft.client.name, draft.client.designation)}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Name — company */}
        {draft.client.clientType === 'professionnel' && (
          <div className="space-y-2">
            <Label>Nom de la société *</Label>
            <Input
              data-testid="client-name-input"
              placeholder="Ex: SPRL Dupont"
              value={draft.client.name}
              onChange={(e) => setClientField('name', e.target.value)}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Personne de contact</Label>
          <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3">
            <div className="space-y-2">
              <Label>Civ.</Label>
              <Select
                value={draft.client.civilityId}
                onValueChange={(v) => setClientField('civilityId', v as ProjectCreateCivilityId)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {civilityOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input 
                data-testid="client-lastname-input" 
                placeholder="Dupont" 
                value={draft.client.lastName} 
                onChange={(e) => {
                  setLastNameWasEmpty(false)
                  setClientField('lastName', e.target.value)
                }} 
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Téléphone *</Label>
            <Input
              data-testid="client-phone-input"
              value={draft.client.phone}
              onChange={(e) => setClientField('phone', e.target.value)}
              placeholder="0475 12 34 56"
            />
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              data-testid="client-email-input"
              value={draft.client.email}
              onChange={(e) => setClientField('email', e.target.value)}
              placeholder="j.dupont@techcorp.be"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Langue des documents</Label>
          {languageOptions.length <= 1 ? (
            // Single-language org: hide the radio (nothing to choose) and just show the label.
            // The wizard parent already seeded documentLanguageId from this single option.
            <p
              className="text-sm text-muted-foreground"
              data-testid="client-language-static"
            >
              {languageOptions[0]?.label ?? '—'}
            </p>
          ) : (
            <RadioGroup
              value={draft.client.documentLanguageId}
              onValueChange={(v) => setClientField('documentLanguageId', v as ProjectCreateLanguageId)}
              className="flex flex-wrap gap-6"
            >
              {languageOptions.map((opt) => (
                <div key={opt.id} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={opt.id}
                    id={`lang_${opt.id}`}
                    data-testid={`client-language-radio-${opt.id}`}
                  />
                  <Label htmlFor={`lang_${opt.id}`}>{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>

        <ProjectCreateNavigation
          currentStep={currentStep}
          onBack={onBack}
          onNext={onNext}
          canGoNext={canGoNext}
        />
      </div>
    </div>
  )
}
